import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FieldLabel } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { CompanyProfileService } from 'libs/client-portal-shared/src/app/modules/companyProfile/services/CompanyProfileService';

const INTERNAL_CLOSER_EDIT_ROLES = [
  'closingSpecialist',
  'closingManager',
  'closingCoordinator',
  'closingCounsel',
  'closingAttorney',
  'tech',
  'techAdmin',
];

const canEditAssignedInternalCloser = (roles: string[] = []) =>
  roles.some(role => INTERNAL_CLOSER_EDIT_ROLES.includes(role));

type CloserOption = { label: string; value: string | number };

interface AssignedInternalCloserFieldProps {
  companyProfileStore: any;
}

/**
 * Sticky Assigned Internal Closer for the TPO (client_success_mappings role=internalCloser).
 * Editable only by internal closing team / tech; saves immediately on change.
 */
export const AssignedInternalCloserField = observer(
  ({ companyProfileStore }: AssignedInternalCloserFieldProps) => {
    const { userStore } = useStore();
    const companyProfileService = useMemo(() => new CompanyProfileService(), []);
    const lenderId =
      companyProfileStore?.salesforceLenderStore?.salesForceLender?.lenderId;
    const roles = userStore?.userInformation?.roles || [];
    const canEdit = canEditAssignedInternalCloser(roles);

    const [options, setOptions] = useState<CloserOption[]>([]);
    const [selected, setSelected] = useState<CloserOption | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (!lenderId) {
        return;
      }
      let cancelled = false;
      const load = async () => {
        setLoading(true);
        try {
          const [optionsRes, membersRes] = await Promise.all([
            companyProfileService.getInternalCloserDropdownOptions(),
            companyProfileService.getClientSuccessMappingsMembersForLender(
              lenderId,
              'internalCloser'
            ),
          ]);
          if (cancelled) {
            return;
          }
          const closerOptions: CloserOption[] =
            optionsRes?.data?.internalClosers || [];
          setOptions(closerOptions);
          const members = membersRes?.data;
          const first = Array.isArray(members) ? members[0] : null;
          if (first?.userId != null) {
            const match =
              closerOptions.find(
                o => String(o.value) === String(first.userId)
              ) || {
                label: first.fullname,
                value: first.userId,
              };
            setSelected(match);
          } else {
            setSelected(null);
          }
        } catch (e) {
          console.error('Failed to load Assigned Internal Closer', e);
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [lenderId, companyProfileService]);

    const handleChange = async (_event, value: CloserOption | null) => {
      if (!canEdit || !lenderId) {
        return;
      }
      const previous = selected;
      setSelected(value);
      try {
        const memberIds = value?.value != null ? String(value.value) : '';
        await companyProfileService.updateClientSuccessMappingsMembersForLender(
          lenderId,
          memberIds,
          'internalCloser'
        );
      } catch (e) {
        console.error('Failed to update Assigned Internal Closer', e);
        setSelected(previous);
      }
    };

    return (
      <>
        <FieldLabel>Assigned Internal Closer</FieldLabel>
        <Autocomplete
          id="assigned-internal-closer"
          options={options}
          value={selected}
          loading={loading}
          disabled={!canEdit || !lenderId}
          getOptionLabel={option => option?.label || ''}
          getOptionSelected={(option, value) =>
            String(option?.value) === String(value?.value)
          }
          onChange={handleChange}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Assigned Internal Closer"
              data-testid="assigned-internal-closer"
            />
          )}
        />
      </>
    );
  }
);
